import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Calculator, TrendingUp, TrendingDown, Users, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConcreteCalculatorCard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/devis/admin/stats?days=30`);
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching calculator stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#C97249] animate-pulse" />
            <CardTitle className="text-slate-100">Concrete Calculator Performance</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-slate-400 text-center py-6">Loading statistics...</div>
        </CardContent>
      </Card>
    );
  }

  if (error || !stats) {
    return (
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[#C97249]" />
            <CardTitle className="text-slate-100">Concrete Calculator Performance</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-red-400 text-center py-6">{error || 'No data available'}</div>
        </CardContent>
      </Card>
    );
  }

  const { summary } = stats;
  const conversionRate = summary.conversion_rate;

  // Determine conversion color
  let conversionColor = 'text-red-400';
  if (conversionRate > 50) {
    conversionColor = 'text-green-400';
  } else if (conversionRate >= 30) {
    conversionColor = 'text-orange-400';
  }

  return (
    <Link to="/admin/devis-analytics">
      <Card className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-all duration-300 hover:scale-105 hover:shadow-2xl cursor-pointer group h-full">
        <CardHeader>
          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#C97249] to-[#B8624A] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
            <Calculator className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-xl text-slate-100 group-hover:text-white transition-colors">
            Calculator Performance
          </CardTitle>
          <CardDescription className="text-slate-400 mt-2">
            Lead generation and conversion tracking
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4">
            {/* Leads Generated */}
            <div className="bg-slate-900/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-green-400" />
                <span className="text-xs text-slate-400">Leads Generated</span>
              </div>
              <div className="text-2xl font-bold text-slate-100">{summary.total_leads}</div>
            </div>

            {/* Abandons */}
            <div className="bg-slate-900/50 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <XCircle className="w-4 h-4 text-red-400" />
                <span className="text-xs text-slate-400">Abandons</span>
              </div>
              <div className="text-2xl font-bold text-slate-100">{summary.total_abandons}</div>
            </div>
          </div>

          {/* Conversion Rate - Prominent */}
          <div className="bg-gradient-to-r from-slate-900/80 to-slate-800/80 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-300 font-medium">Conversion Rate</span>
              {conversionRate > 50 ? (
                <TrendingUp className="w-5 h-5 text-green-400" />
              ) : (
                <TrendingDown className="w-5 h-5 text-orange-400" />
              )}
            </div>
            <div className={`text-4xl font-bold mt-2 ${conversionColor}`}>
              {conversionRate.toFixed(1)}%
            </div>
          </div>

          {/* Average Project Values */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-400">Avg. Lead Value:</span>
              <span className="text-green-400 font-semibold">{summary.avg_total_leads.toFixed(0)}€</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-400">Avg. Abandon Value:</span>
              <span className="text-red-400 font-semibold">{summary.avg_total_abandons.toFixed(0)}€</span>
            </div>
          </div>

          {/* Footer Note */}
          <div className="pt-3 border-t border-slate-700">
            <p className="text-xs text-slate-500 text-center">
              Data based on concrete price calculator interactions (last 30 days)
            </p>
          </div>

          {/* Arrow indicator */}
          <div className="flex items-center justify-center">
            <span className="text-[#C97249] group-hover:translate-x-2 transition-transform text-lg">
              → View Details
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ConcreteCalculatorCard;
